import { Button, Form, Input, Modal, Popover, Row, Select, Space, Switch } from 'antd';
import PageHeader from 'components/PageHeader';
import * as yup from 'yup';
import './webhookModal.scss';
import Drawer from 'components/Drawer';
import useWindowDimensions from 'hooks/useWindowDimensions';
import { WebhookEventNames } from '../constants';
import { InfoIcon2 } from 'components/Icons';

export const WebhookModal = ({
  isOpen,
  onCancel,
  data: { isEdit, data },
  onUpdate,
  onCreate = {},
  loading,
}) => {
  const { width } = useWindowDimensions();
  const [form] = Form.useForm();

  const webhookEventsList = [
    {
      label: WebhookEventNames.PROPOSAL_CREATED,
      value: WebhookEventNames.PROPOSAL_CREATED,
    },
    {
      label: WebhookEventNames.PROPOSAL_PUBLISHED,
      value: WebhookEventNames.PROPOSAL_PUBLISHED,
    },
    {
      label: WebhookEventNames.PROPOSAL_CONTENT_UPDATED,
      value: WebhookEventNames.PROPOSAL_CONTENT_UPDATED,
    },
    {
      label: WebhookEventNames.PROPOSAL_UNPUBLISHED,
      value: WebhookEventNames.PROPOSAL_UNPUBLISHED,
    },
    {
      label: WebhookEventNames.PROPOSAL_OPENED,
      value: WebhookEventNames.PROPOSAL_OPENED,
    },
    {
      label: WebhookEventNames.PROPOSAL_SIGNED,
      value: WebhookEventNames.PROPOSAL_SIGNED,
    },
    {
      label: WebhookEventNames.PROPOSAL_STATUS_CHANGED,
      value: WebhookEventNames.PROPOSAL_STATUS_CHANGED,
    },
    {
      label: WebhookEventNames.PROPOSAL_DECLINED,
      value: WebhookEventNames.PROPOSAL_DECLINED,
    },
    {
      label: WebhookEventNames.PROPOSAL_DELETED,
      value: WebhookEventNames.PROPOSAL_DELETED,
    },
  ];

  const schema = yup.object().shape({
    name: yup
      .string()
      .required('The name is required.')
      .min(3, 'The name must be at least 3 characters long.')
      .max(100, 'The name must be less than 100 characters long.'),
    events: yup
      .array()
      .of(yup.string().trim().required('Event is required.'))
      .min(1, 'At least one event is required.')
      .required('Events are required.'),
    url: yup.string().trim().required('The URL is required.').url('Please enter a valid URL.'),
    encrypted: yup.boolean().default(false),
  });

  const yupSync = {
    async validator({ field }, value) {
      await schema.validateSyncAt(field, { [field]: value });
    },
  };

  const content = (
    <>
      {' '}
      <PageHeader pageTitle={isEdit ? 'Edit webhook' : 'New webhook'} />
      <Form
        form={form}
        style={{
          width: '100%',
        }}
        initialValues={data}
        layout="vertical "
        className="webhook-modal-form"
        autoComplete="off"
        onFinish={(values) => {
          isEdit ? onUpdate({ ...data, ...values }) : onCreate(values);
        }}>
        <Row className="form-content">
          <Form.Item label="Webhook name" name="name" rules={[yupSync]}>
            <Input />
          </Form.Item>
          <Form.Item label="Webhook URL" name="url" rules={[yupSync]}>
            <Input />
          </Form.Item>
          <Form.Item label="Events" name="events" rules={[yupSync]}>
            <Select
              mode="multiple"
              showArrow
              style={{ width: '100%' }}
              placeholder="Select..."
              options={webhookEventsList}
              optionRender={(option) => (
                <Space>
                  <span role="img" aria-label={option.data.label}>
                    {option.data.emoji}
                  </span>
                  {option.data.desc}
                </Space>
              )}
              dropdownMatchSelectWidth={false} // ✅ prevents layout issues on mobile
              getPopupContainer={(trigger) => trigger.parentNode} // ✅ keeps dropdown inside parent
            />
          </Form.Item>
        </Row>
        <Row className="webhook-modal-form-buttons" justify="space-between" align="middle">
          <Row className="webhook-encrypted-switch-row" wrap={false}>
            <Form.Item
              className="webhook-encrypted-switch-form-item"
              valuePropName="checked"
              noStyle
              name="encrypted"
              rules={[yupSync]}>
              <Switch className="webhook-encrypted-switch" />
            </Form.Item>
            <span className="label-text">Encrypt</span>
            <Popover
              overlayClassName="encrypted-switch-webhook-popover"
              content={
                <span>
                  When enabled, data sent to this webhook is encrypted with your
                  <br /> secret key. Disable if your endpoint requires plain JSON.
                </span>
              }>
              <InfoIcon2 />
            </Popover>
          </Row>
          <Row className="action-buttons-row">
            <Button
              className="large-btn grey-btn"
              style={{ textTransform: 'uppercase' }}
              onClick={onCancel}>
              Cancel
            </Button>
            <Button
              className="large-btn secondary-btn"
              style={{ textTransform: 'uppercase' }}
              htmlType="submit"
              loading={loading}>
              {isEdit ? 'SAVE' : 'CREATE'}
            </Button>
          </Row>
        </Row>
      </Form>
    </>
  );

  // if smaller device render slider
  if (width < 768) {
    return (
      <>
        <Drawer
          visible={isOpen}
          setVisible={onCancel}
          content={content}
          direction="bottom"
          className="webhook-modal"
        />
      </>
    );
  }

  return isOpen ? (
    <Modal
      centered
      className="customize-email-modal webhook-modal"
      visible={isOpen}
      width={'564px'}
      onCancel={onCancel}
      maskClosable={false}
      footer={false}>
      {content}
    </Modal>
  ) : (
    <></>
  );
};
